import React, { memo, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';

import { CreateTimeCardModal } from 'Components/TimeTracking';

import {
  createTimeCard,
  listAllEmployeesForModals,
  listAllProjectsForModals,
} from 'Containers/TimeTracking/actions';

import { useTimeTrackingFunctions } from 'Context/TimeTracking';
import { timeCardTypes } from 'Containers/TimeTracking/timeCardTypes';
import { createTimeCardErrorSelector } from 'Containers/TimeTracking/selectors';
import { firstCompanyIdSelector } from 'Containers/Projects/selectors';

const CreateTimeCardModalContainer = () => {
  const dispatch = useDispatch();

  const [selectedEmployeeId, setSelectedEmployeeId] = useState(undefined);
  const [selectedProjectId, setSelectedProjectId] = useState(undefined);
  const [timeIn, setTimeIn] = useState(undefined);
  const [timeOut, setTimeOut] = useState(undefined);

  const { isCreateTimeCardModalOpen, employeeDropdownItems, setIsCreateTimeCardModalOpen, projectDropdownItems }: any =
    useTimeTrackingFunctions();

  const errors = useSelector(createTimeCardErrorSelector, areEqual);
  const firstCompanyId = useSelector(firstCompanyIdSelector, areEqual);

  // Load employees and projects when modal opens
  useEffect(() => {
    if (isCreateTimeCardModalOpen && firstCompanyId) {
      dispatch(listAllEmployeesForModals(firstCompanyId));
      dispatch(listAllProjectsForModals(firstCompanyId));
    }
  }, [isCreateTimeCardModalOpen, firstCompanyId]);

  const resetInputs = () => {
    setSelectedEmployeeId(undefined);
    setSelectedProjectId(undefined);
    setTimeIn(undefined);
    setTimeOut(undefined);
  };

  const closeModal = () => {
    setIsCreateTimeCardModalOpen(false);
    resetInputs();
  };

  const onCreateButtonClick = useCallback(() => {
    dispatch(
      createTimeCard(selectedProjectId, timeIn?.toISOString(), timeCardTypes.standard, timeOut?.toISOString(), selectedEmployeeId, closeModal)
    );
  }, [selectedProjectId, selectedEmployeeId, timeIn, timeOut]);

  return (
    <CreateTimeCardModal
      // modal variables
      isOpen={isCreateTimeCardModalOpen}
      formErrors={errors}
      canCreate={selectedEmployeeId && selectedProjectId && timeIn && timeOut}
      // employee variables
      employees={employeeDropdownItems || []}
      selectedEmployeeId={selectedEmployeeId}
      // project variables
      projects={projectDropdownItems || []}
      selectedProjectId={selectedProjectId}
      // date variables
      startDate={timeIn}
      endDate={timeOut}
      // modal functions
      modalCloseClick={closeModal}
      onCreateButtonClick={onCreateButtonClick}
      // employee functions
      setSelectedEmployee={setSelectedEmployeeId}
      // project functions
      setSelectedProject={setSelectedProjectId}
      // date functions
      onChangeStartDate={setTimeIn}
      onChangeEndDate={setTimeOut}
    />
  );
};

const CreateTimeCardModalContainerMemo = memo(CreateTimeCardModalContainer, areEqual);

export { CreateTimeCardModalContainerMemo as CreateTimeCardModal };
